import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { addNewUser, updateUserData } from "../../store/actions/UserActions";
import { AppDispatch, RootState } from "../../store";
import { MedicalDegree, UserRole, UserTag } from "../../ts/enums";
import { navigationBack, validateEmail } from "../../utils";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import classes from "./CreateUser.module.css";
import User from "../../ts/models/User";
import Input from "../../components/Inputs/Input";
import PanelContainer from "../../components/Containers/PanelContainer";
import PanelInnerContainer from "../../components/Containers/PanelInnerContainer";
import InputLabel from "../../components/Inputs/InputLabel";
import SelectButton from "../../components/Buttons/SelectButton";
import FormBox from "../../components/Box/FormBox";
import helpIcon from "../../assets/icons/help-info.svg";
import LinkButton from "../../components/Buttons/LinkButton";
import HelpModal from "../../components/Help/HelpModal";
import Select from "../../components/Inputs/Select";
import Checkbox from "../../components/Checkbox/Checkbox";

const roles = [
  { label: "Select ", value: "" },
  { label: "Admin", value: UserRole.Admin },
  { label: "Clinical", value: UserRole.Clinical },
  { label: "Non Clinical", value: UserRole.NonClinical },
  { label: "Non Clinical Plus", value: UserRole.NonClinicalPlus },
];

const medicalDegrees = [
  { id: MedicalDegree.MD },
  { id: MedicalDegree.DO },
  { id: MedicalDegree.APN },
  { id: MedicalDegree.PA },
  { id: MedicalDegree.Other },
];

const tags = [{ id: UserTag.TeamLead }, { id: UserTag.NurseLead }];

type props = {};

const getInitialFieldErrors = () => {
  return {
    firstName: false,
    lastName: false,
    email: false,
    mobileNo: false,
    role: false,
  };
};

const CreateUser: React.FC<props> = () => {
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const userToBeEdited = useSelector(
    (state: RootState) => state.users.userToBeEdited
  );
  const services = useSelector((state: RootState) => state.masters.services);

  const [data, setData] = useState<User>(userToBeEdited);
  const [fieldErrors, setFieldErrors] = useState(getInitialFieldErrors());
  const [showHelpModal, setShowHelpModal] = useState(false);

  const activeServices = services.filter((service) => {
    return service.isActive;
  });

  const handleBack = (addOrUpdateCanceled: boolean) => {
    navigationBack(location, navigate, { addOrUpdateCanceled });
  };

  const isFieldsValid = (): boolean => {
    const fieldErrors = getInitialFieldErrors();

    if (!data.firstName) {
      fieldErrors.firstName = true;
    }
    if (!data.lastName) {
      fieldErrors.lastName = true;
    }
    if (!data.email || !validateEmail(data.email)) {
      fieldErrors.email = true;
    }

    const phoneRegex = /^(?:\+1\d{10}|\d{10}|\+91\d{10})$/;

    if (!data.mobileNo || !phoneRegex.test(data.mobileNo)) {
      fieldErrors.mobileNo = true;
    }
    if (!data.role) {
      fieldErrors.role = true;
    }

    setFieldErrors(fieldErrors);

    return Object.values(fieldErrors).every((value) => value === false);
  };

  const clearFieldErrors = (key: keyof typeof fieldErrors) => {
    if (fieldErrors[key])
      setFieldErrors((prevState) => {
        const newState = { ...prevState };
        newState[key] = false;
        return newState;
      });
  };

  const addOrEditUserHandler = async (isEdit = false) => {
    if (!isFieldsValid()) {
      return;
    }

    if (isEdit) {
      dispatch(updateUserData(data, () => handleBack(false)));
    } else {
      dispatch(addNewUser(data, () => handleBack(false)));
    }
  };

  return (
    <>
      {showHelpModal && (
        <HelpModal
          title='Create User'
          content={`/api/storage/cimpar/resources/help/create-user-help.html`}
          onClose={() => setShowHelpModal(false)}
        />
      )}
      <PanelContainer
        name={`${userToBeEdited.id ? "Update" : "Create New"} User`}
        backBtn>
        <div className={classes.helpContainer}>
          <LinkButton
            label={"Know About How To Create User"}
            icon={<img src={helpIcon} alt='' />}
            onClick={() => setShowHelpModal(true)}
            className={classes.helpBtn}
          />
        </div>
        <PanelInnerContainer>
          <FormBox name='Basic Details'>
            <div className={classes.boxContainer}>
              <div>
                <Input
                  name='First Name'
                  value={data.firstName}
                  onChange={(e) => {
                    setData((prevState) => {
                      const newState = { ...prevState };
                      newState.firstName = e.target.value;
                      return newState;
                    });
                    clearFieldErrors("firstName");
                  }}
                  isInvalid={fieldErrors.firstName}
                  className={classes.input}
                />
              </div>
              <div>
                <Input
                  name='Middle Name'
                  value={data.middleName}
                  onChange={(e) => {
                    setData((prevState) => {
                      const newState = { ...prevState };
                      newState.middleName = e.target.value;
                      return newState;
                    });
                  }}
                  className={classes.input}
                />
              </div>
              <div>
                <Input
                  name='Last Name'
                  value={data.lastName}
                  onChange={(e) => {
                    setData((prevState) => {
                      const newState = { ...prevState };
                      newState.lastName = e.target.value;
                      return newState;
                    });
                    clearFieldErrors("lastName");
                  }}
                  isInvalid={fieldErrors.lastName}
                  className={classes.input}
                />
              </div>
              <div>
                <Input
                  name='Email'
                  value={data.email}
                  onChange={(e) => {
                    setData((prevState) => {
                      const newState = { ...prevState };
                      newState.email = e.target.value;
                      return newState;
                    });
                    clearFieldErrors("email");
                  }}
                  isInvalid={fieldErrors.email}
                  invalidText='Invalid email'
                  className={classes.input}
                  disabled={!!data.id}
                />
              </div>
              <div>
                <Select
                  label='Role of the user'
                  htmlFor='role'
                  options={roles}
                  nameExtractor='label'
                  valueExtractor='value'
                  value={data.role}
                  onChange={(e) => {
                    setData((prevState) => {
                      const newState = { ...prevState };
                      newState.role = e.target.value;
                      return newState;
                    });
                    clearFieldErrors("role");
                  }}
                  className={classes.input}
                  isInvalid={fieldErrors.role}
                />
              </div>
              <div>
                <Input
                  name='Default Location'
                  value={data.defaultLocation}
                  onChange={(e) => {
                    setData((prevState) => {
                      const newState = { ...prevState };
                      newState.defaultLocation = e.target.value;
                      return newState;
                    });
                  }}
                  className={classes.input}
                />
              </div>
              <Input
                name='State License ID'
                value={data.licenseId}
                onChange={(e) => {
                  setData((prevState) => {
                    const newState = { ...prevState };
                    newState.licenseId = e.target.value;
                    return newState;
                  });
                }}
                className={classes.input}
              />
              <Input
                name='State Certificate License Level'
                value={data.licenseLevel}
                onChange={(e) => {
                  setData((prevState) => {
                    const newState = { ...prevState };
                    newState.licenseLevel = e.target.value;
                    return newState;
                  });
                }}
                className={classes.input}
              />
              <Checkbox
                className={classes.allowSigningDocCheckbox}
                checked={data.allowSigningDocuments}
                id={"allow-signing-document"}
                label={"Allow signing documents"}
                onChange={(checked) =>
                  setData((prevState) => {
                    const newState = { ...prevState };
                    newState.allowSigningDocuments = checked;
                    return newState;
                  })
                }
              />
            </div>
            <InputLabel
              className={classes.label}
              label='Select Medical Service'
            />
            <div className={classes.servicesContainer}>
              {activeServices.map((service, idx) => (
                <SelectButton
                  key={idx}
                  name={service.name}
                  onClick={() => {
                    setData((state) => {
                      const prevServiceType = [...state.serviceTypes];
                      let newServiceType: string[] = [...prevServiceType];
                      if (prevServiceType.includes(service.id)) {
                        newServiceType = prevServiceType.filter(
                          (serviceId) => serviceId !== service.id
                        );
                      } else {
                        newServiceType.push(service.id);
                      }
                      return {
                        ...state,
                        serviceTypes: newServiceType,
                      };
                    });
                  }}
                  active={data.serviceTypes.includes(service.id)}
                />
              ))}
            </div>
            <InputLabel
              className={classes.label}
              label='Medical degree of the user'
            />
            <div className={classes.rolesContainer}>
              {medicalDegrees.map((item, idx) => (
                <SelectButton
                  key={idx}
                  name={item.id}
                  onClick={() => {
                    setData((prevState) => {
                      const newState = { ...prevState };
                      newState.medicalDegree = item.id;
                      return newState;
                    });
                  }}
                  active={item.id === data.medicalDegree}
                />
              ))}
            </div>
            <InputLabel className={classes.label} label='Select Tags' />
            <div className={classes.servicesContainer}>
              {tags.map((tag, idx) => (
                <SelectButton
                  key={idx}
                  name={tag.id}
                  onClick={() => {
                    setData((state) => {
                      const prevTags = state?.tags || [];
                      let newTags: string[] = [...prevTags];
                      if (prevTags?.includes(tag.id)) {
                        newTags = prevTags?.filter((id) => id !== tag.id);
                      } else {
                        newTags.push(tag.id);
                      }
                      return {
                        ...state,
                        tags: newTags,
                      };
                    });
                  }}
                  active={data.tags?.includes(tag.id) || false}
                />
              ))}
            </div>
          </FormBox>
          <FormBox name='Contact Information'>
            <div className={classes.boxContainer}>
              <Input
                name='Mobile Phone'
                type='text'
                value={data.mobileNo}
                onChange={(e) => {
                  const regex = /^[+\d]*$/;
                  if (regex.test(e.target.value)) {
                    setData((prevState) => {
                      const newState = { ...prevState };
                      newState.mobileNo = e.target.value;
                      return newState;
                    });
                  }
                  clearFieldErrors("mobileNo");
                }}
                isInvalid={fieldErrors.mobileNo}
                invalidText='Invalid number'
                className={classes.input}
              />
            </div>
          </FormBox>
          <div className={classes.actions}>
            <PrimaryButton
              className={classes.actionBtn}
              name='Cancel'
              onClick={() => handleBack(true)}
              transparent
              style={{ marginRight: "1rem" }}
            />
            <PrimaryButton
              className={classes.actionBtn}
              name={userToBeEdited.id ? "Update User" : "Create User"}
              onClick={() => addOrEditUserHandler(!!userToBeEdited.id)}
              style={{ marginRight: "1rem" }}
            />
          </div>
        </PanelInnerContainer>
      </PanelContainer>
    </>
  );
};

export default CreateUser;
